'use client';

import type { ComponentPropsWithRef, ReactNode } from 'react';
import {
    Provider as TooltipProvider,
    Root as TooltipRoot,
    Trigger as TooltipTrigger,
    Portal as TooltipPortal,
    Content as TooltipContent,
    Arrow as TooltipArrow,
} from '@radix-ui/react-tooltip';
import { cn } from '@/lib/utils';
import { Button, type ButtonProps } from '@/shared-ui/atoms/button/button';
import { Typography, type TypographyProps } from '@/shared-ui/atoms/typography/typography';

// Provider -------------------------------------------------------------------

type ProviderProps = ComponentPropsWithRef<typeof TooltipProvider>;

const Provider = ({ delayDuration = 200, ...props }: ProviderProps) => (
    <TooltipProvider delayDuration={delayDuration} {...props} />
);
Provider.displayName = 'Tooltip.Provider';

// Root -----------------------------------------------------------------------

type RootProps = ComponentPropsWithRef<typeof TooltipRoot>;

// NOTE: Provider is scoped per Root for convenience. Radix recommends placing a single
// Tooltip.Provider at the app root (e.g. src/routes/_app.tsx) so that delayDuration is
// skipped when a user quickly moves between tooltips. Consider moving it there in future.
const Root = ({ ...props }: RootProps) => (
    <Provider>
        <TooltipRoot {...props} />
    </Provider>
);
Root.displayName = 'Tooltip.Root';

// Trigger --------------------------------------------------------------------

type TriggerProps = ComponentPropsWithRef<typeof TooltipTrigger>;

const Trigger = ({ ...props }: TriggerProps) => <TooltipTrigger data-slot="tooltip-trigger" {...props} />;
Trigger.displayName = 'Tooltip.Trigger';

// Title ----------------------------------------------------------------------

type TitleProps = Omit<TypographyProps, 'variant' | 'weight' | 'as'>;

const Title = ({ className, ref, ...props }: TitleProps) => (
    <Typography
        data-slot="tooltip-title"
        variant="h5"
        weight="regular"
        as="p"
        ref={ref as never}
        className={cn(className)}
        {...props}
    />
);
Title.displayName = 'Tooltip.Title';

// Body -----------------------------------------------------------------------

type BodyProps = Omit<TypographyProps, 'variant' | 'weight' | 'as'>;

const Body = ({ className, ref, ...props }: BodyProps) => (
    <Typography
        data-slot="tooltip-body"
        variant="small"
        weight="light"
        as="div"
        ref={ref as never}
        className={cn('[&_ul]:list-disc [&_ul]:pl-5', className)}
        {...props}
    />
);
Body.displayName = 'Tooltip.Body';

// Action ---------------------------------------------------------------------

type ActionProps = Omit<ButtonProps, 'variant' | 'size' | 'price' | 'originalPrice'>;

const Action = ({ className, ...props }: ActionProps) => (
    <Button data-slot="tooltip-action" variant="tertiary" size="sm" className={cn(className)} {...props} />
);
Action.displayName = 'Tooltip.Action';

// Content --------------------------------------------------------------------

type ContentProps = ComponentPropsWithRef<typeof TooltipContent> & {
    children: ReactNode;
};

const Content = ({ className, sideOffset = 8, children, ...props }: ContentProps) => (
    <TooltipPortal>
        <TooltipContent
            data-slot="tooltip-content"
            sideOffset={sideOffset}
            className={cn(
                'bg-background text-foreground',
                'flex flex-col gap-4 p-4',
                'rounded-sm filter-floating',
                'w-[253px] md:w-[311px]',
                'z-50',
                'animate-in fade-in-0 zoom-in-95',
                'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
                className
            )}
            {...props}>
            {children}
            <TooltipArrow width={12} height={8} className="fill-background" />
        </TooltipContent>
    </TooltipPortal>
);
Content.displayName = 'Tooltip.Content';

// Namespace export -----------------------------------------------------------

const Tooltip = { Provider, Root, Trigger, Content, Title, Body, Action } as const;

export { Tooltip };
